'use client';

import LibUserMenu from '@umodeapporg/ui/components/UI/UserMenu';
import { useNotification } from '@umodeapporg/ui/hooks/useNotification';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'zustand';

import { apiErrorMessage } from '@/app/apontamento/helpers/lancados';
import { useContaAtiva } from '@/domain/auth/hooks/useContaAtiva';
import { useCurrentUserRole } from '@/domain/auth/hooks/useCurrentUserRole';
import { authRepository } from '@/domain/auth/repositories/authRepository';
import { logout } from '@/infrastructure/http/authRefresh';
import { setActivePartnerId } from '@/infrastructure/http/gatewayFetch';
import { logger } from '@/infrastructure/persistence/managers/LoggerManager';
import { authStore } from '@/infrastructure/store/authStore';
import { resetPartnerScopedStores } from '@/lib/zustand';

export function UserMenu() {
  const { t } = useTranslation('common');
  const { notify } = useNotification();
  const [open, setOpen] = useState(false);
  const [switchingId, setSwitchingId] = useState<string | null>(null);

  const user = useStore(authStore, (s) => s.user);
  const setUser = useStore(authStore, (s) => s.setUser);
  const partnersList = useStore(authStore, (s) => s.partnersList);
  const setUserClearanceRoles = useStore(authStore, (s) => s.setUserClearanceRoles);
  const { roleLabel } = useCurrentUserRole();
  // The account as registered in the uMode dashboard: the same name and logo as the header.
  const conta = useContaAtiva();

  if (!user) return null;

  const handleSwitchAccount = async (partnerId: string): Promise<void> => {
    if (partnerId === user.partnerId || switchingId) return;

    setSwitchingId(partnerId);
    try {
      const updatedUser = await authRepository.switchPartner(partnerId);

      // Everything on screen belongs to the previous account. It is cleared BEFORE the new
      // account reaches the auth store: that change remounts the screen (AuthGuard) and reloads
      // the shell's session data, so the first render under the new name is empty, never stale.
      resetPartnerScopedStores();
      setUserClearanceRoles([]);
      setActivePartnerId(updatedUser.partnerId);
      setUser(updatedUser);

      // The catalog is scoped to the partner, so the role label of the previous one would be
      // wrong (or absent) here. A failure only costs the friendly text, never the switch.
      try {
        setUserClearanceRoles(await authRepository.getUserClearanceRoles(updatedUser.partnerId));
      } catch (rolesError) {
        logger.warn('[UserMenu] Failed to refresh clearance roles after switching partner', rolesError);
        setUserClearanceRoles([]);
      }
      setOpen(false);
    } catch (error) {
      logger.error('[UserMenu] Failed to switch partner', error);
      notify({ message: apiErrorMessage(error, t('acesso.trocarConta.erro')), type: 'error' });
    } finally {
      setSwitchingId(null);
    }
  };

  return (
    <LibUserMenu
      open={open}
      onOpenChange={setOpen}
      userName={user.name}
      userEmail={user.email}
      accountName={conta.nome === '' ? undefined : conta.nome}
      accountLogoUrl={conta.logoUrl ?? undefined}
      roleLabel={roleLabel}
      accounts={partnersList.map((partner) => ({
        id: partner.partnerId,
        name: partner.partnerName,
        logoUrl: partner.partnerLogoImage?.s600,
      }))}
      activeAccountId={user.partnerId}
      switchingAccountId={switchingId}
      onSwitchAccount={(partnerId) => {
        void handleSwitchAccount(partnerId);
      }}
      onLogout={logout}
    />
  );
}
